import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery, DEFAULT_MAX_RETRIES as maxRetries } from "../retryingBaseQuery";
import fetchAllPages from "../../utils/fetchAllPages";
import GETJwtToken from "../authentication/authentication";
import { retryUnlessClientError } from "../retryConditions";

/** A short-lived appliance claim awaiting or carrying administrator approval. */
export interface EdgeEnrollment {
  id: string;
  server_id: string;
  organization_id: string;
  appliance_name: string;
  organization_name: string;
  claim_code: string;
  state: "pending" | "approved";
  expires_at: string;
  requested_at: string;
}

/** JSON envelope returned by the edge enrollment list endpoint. */
export interface EdgeEnrollmentResponse {
  status: "success";
  data: EdgeEnrollment[];
}

/** Physical server inventory managed before a live appliance identity exists. */
export interface EdgeAppliance {
  id: string;
  organization_id: string;
  server_id?: string;
  asset_tag: string;
  hostname: string;
  target_disk: string;
  chassis_serial?: string;
  compatible: string;
  state: "ready" | "package_issued" | "installed" | "activated" | "retired";
  package_revision: number;
  package_issued_at?: string;
  activated_at?: string;
  created_at: string;
  organization?: { id: string; name: string };
}

/** JSON envelope returned by the physical appliance inventory endpoint. */
export interface EdgeApplianceResponse {
  status: "success";
  data: EdgeAppliance[];
}

/** Published reusable factory USB image for provisioning compatible servers. */
export interface EdgeFactoryImage {
  image_id: string;
  environment: string;
  compatible: string;
  key_id: string;
  sha256: string;
  bytes: number;
  built_utc: string;
  download_url: string;
  /** Present when the image was published with its standalone operator tools zip. */
  tools_download_url?: string;
  tools_sha256?: string;
  expires_at: string;
}

/** Values accepted when registering one physical appliance. */
export interface CreateEdgeAppliance {
  organization_id: string;
  asset_tag: string;
  hostname: string;
  target_disk: string;
  chassis_serial?: string;
}

/** Published signed edge release offered for appliance update assignments. */
export interface EdgeUpdateRelease {
  id: string;
  version: string;
  sequence: number;
  published_at: string;
  paused_at?: string | null;
  pause_reason?: string | null;
  bundle_size: number;
}

/** JSON envelope returned by the edge release list endpoint. */
export interface EdgeUpdateReleaseResponse {
  status: "success";
  data: EdgeUpdateRelease[];
}

/** Rollout ring, mirroring the backend lab → canary → pilot → general order. */
export type EdgeUpdateRing = "lab" | "canary" | "pilot" | "general";

/** Values accepted when assigning one release rollout window to an appliance. */
export interface EdgeUpdateAssignmentBody {
  server_id: string;
  release_id: string;
  ring: EdgeUpdateRing;
  not_before: string;
  deadline: string;
}

const retryingBaseQuery = createRetryingBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
  maxRetries,
);

// Edge-enrollment endpoints speak JSON and authenticate with the browser's
// Cognito token instead of the flatbuffer session, so they carry their own
// Authorization header; a missing session surfaces as a plain 401.
const authenticatedTransport: typeof retryingBaseQuery = async (args, api, extraOptions) => {
  const request = typeof args === "string" ? { url: args } : args;
  if (!request.url.startsWith("/edge-enrollments/")) {
    return retryingBaseQuery(args, api, extraOptions);
  }
  try {
    return await retryingBaseQuery(
      {
        ...request,
        headers: {
          authorization: await GETJwtToken(),
          Accept: "application/json",
        },
      },
      api,
      extraOptions,
    );
  } catch {
    return { error: { status: 401, data: { message: "Authentication required" } } };
  }
};

const baseQuery = authenticatedTransport;

// Claim-code polling and approvals must not resend 4xx refusals; the runtime
// honors this through retryingBaseQuery's requestOptions.
const enrollmentRetry = { retryCondition: retryUnlessClientError(maxRetries) };

export const serverAPI = createApi({
  reducerPath: "serverAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [
    "tagGetServers",
    "tagGetServersLight",
    "tagPostServer",
    "tagPutServer",
    "tagDeleteServer",
    "tagGetSecretClientToken",
    "tagGetEdgeProvisionConfig",
    "tagEdgeEnrollments",
    "tagEdgeAppliances",
    "tagEdgeUpdateReleases",
  ],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL SERVERS */
    getServers: builder.query({
      query: (body: any) => ({
        url: `/servers/?sort=name&sort_direction=asc&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* GET ALL SERVERS LIGHT */
    getServersLight: builder.query({
      query: (body: any) => ({
        url: `/servers/light/${body.orgId ?? ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetServersLight"],
    }),
    //* *********************************************************** */
    //* GET ONE SERVERS */
    getServer: builder.query({
      query: (body: any) => ({
        url: `/servers/${body.idServer}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* GET SERVERS BY ORGANIZATION */
    getServersByOrganization: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/servers/?filter_target=org_id&filter=${body.orgId}&page=1&limit=500`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* POST SECRET CLIENT TOKEN */
    postSecretClientToken: builder.mutation({
      query: (body: any) => ({
        url: "/servers/oauth",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* CREATE SERVER */
    postServer: builder.mutation({
      query: (body: any) => ({
        url: "/servers/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateServer" },
      invalidatesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* EDIT SERVER */
    putServer: builder.mutation({
      queryFn: async (body: any, _queryApi, _extraOptions, fetchWithBQ) => {
        if (!body.body.id) {
          return {
            error: {
              status: 400,
              data: { message: "Server ID is required for update" },
            },
          };
        }
        return fetchWithBQ({
          url: `/servers/${body.body.id}`,
          method: "PUT",
          body: body.body,
          headers: { authorization: body.token },
        });
      },
      extraOptions: { fbSerializer: "UpdateServer" },
      invalidatesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* DELETE SERVER */
    deleteServer: builder.mutation({
      queryFn: async (body: any, _queryApi, _extraOptions, fetchWithBQ) => {
        if (!body.id) {
          return {
            error: {
              status: 400,
              data: { message: "Server ID is required for delete" },
            },
          };
        }
        return fetchWithBQ({
          url: `/servers/${body.id}`,
          method: "DELETE",
          headers: { authorization: body.token },
        });
      },
      invalidatesTags: ["tagGetServers"],
    }),

    //* *********************************************************** */
    //* EXPORT SERVER INFO*/
    getExportServerInfo: builder.query({
      query: (body: any) => ({
        url: `/servers/export/${body.serverId}`,
        headers: { authorization: body.token },
        responseHandler: async (response) => {
          if (!response.ok) throw new Error("Error retrieving the file.");
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition") || "";
          return { blob, contentDisposition };
        },
      }),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* EDGE PROVISIONING - GET CONFIG BY MAC */
    getEdgeProvisionConfig: builder.query({
      query: (body: any) => ({
        url: `/edge/config/${body.mac}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetEdgeProvisionConfig"],
    }),
    //* *********************************************************** */
    //* EDGE PROVISIONING - UPDATE STATUS */
    patchEdgeProvisionStatus: builder.mutation({
      query: (body: any) => ({
        url: `/edge/status/${body.serverId}`,
        method: "PATCH",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* APPLIANCE CLAIM-CODE ENROLLMENT */
    getEdgeEnrollments: builder.query<EdgeEnrollmentResponse, void>({
      query: () => ({ url: "/edge-enrollments/" }),
      extraOptions: enrollmentRetry,
      providesTags: ["tagEdgeEnrollments"],
    }),
    approveEdgeEnrollment: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/edge-enrollments/${id}/approve`,
        method: "POST",
      }),
      extraOptions: enrollmentRetry,
      invalidatesTags: ["tagEdgeEnrollments"],
    }),
    rejectEdgeEnrollment: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/edge-enrollments/${id}/reject`,
        method: "POST",
      }),
      extraOptions: enrollmentRetry,
      invalidatesTags: ["tagEdgeEnrollments"],
    }),
    //* *********************************************************** */
    //* PHYSICAL EDGE APPLIANCE INVENTORY */
    getEdgeAppliances: builder.query<EdgeApplianceResponse, { token: string }>({
      query: ({ token }) => ({
        url: "/edge-appliances/",
        headers: { authorization: token, Accept: "application/json" },
      }),
      providesTags: ["tagEdgeAppliances"],
    }),
    getLatestFactoryImage: builder.query<
      { status: "success"; data: EdgeFactoryImage },
      { token: string }
    >({
      query: ({ token }) => ({
        url: "/edge-appliances/factory-image/latest",
        headers: { authorization: token, Accept: "application/json" },
      }),
      extraOptions: enrollmentRetry,
    }),
    createEdgeAppliance: builder.mutation<
      { status: "success"; data: EdgeAppliance },
      { token: string; body: CreateEdgeAppliance }
    >({
      query: ({ token, body }) => ({
        url: "/edge-appliances/",
        method: "POST",
        body,
        headers: { authorization: token, Accept: "application/json" },
      }),
      invalidatesTags: ["tagEdgeAppliances"],
    }),
    deleteEdgeAppliance: builder.mutation<unknown, { token: string; id: string }>({
      query: ({ token, id }) => ({
        url: `/edge-appliances/${id}`,
        method: "DELETE",
        headers: { authorization: token, Accept: "application/json" },
      }),
      invalidatesTags: ["tagEdgeAppliances"],
    }),
    getEdgeAppliancePackage: builder.query<
      { blob: Blob; contentDisposition: string },
      { token: string; id: string }
    >({
      query: ({ token, id }) => ({
        url: `/edge-appliances/${id}/package`,
        headers: { authorization: token, Accept: "application/vnd.liftnet.provision+json" },
        responseHandler: async (response) => {
          if (!response.ok) throw new Error("Error retrieving the factory package.");
          return {
            blob: await response.blob(),
            contentDisposition: response.headers.get("Content-Disposition") || "",
          };
        },
      }),
      providesTags: ["tagEdgeAppliances"],
    }),
    //* *********************************************************** */
    //* EDGE RELEASE UPDATE ROLLOUT */
    getEdgeUpdateReleases: builder.query<EdgeUpdateReleaseResponse, { token: string }>({
      query: ({ token }) => ({
        url: "/appliance-update/releases",
        headers: { authorization: token, Accept: "application/json" },
      }),
      providesTags: ["tagEdgeUpdateReleases"],
    }),
    createEdgeUpdateAssignment: builder.mutation<
      unknown,
      { token: string; body: EdgeUpdateAssignmentBody }
    >({
      query: ({ token, body }) => ({
        url: "/appliance-update/assignments",
        method: "POST",
        body,
        headers: { authorization: token, Accept: "application/json" },
      }),
    }),
  }),
});

export const {
  useLazyGetServersQuery,
  useGetServersLightQuery,
  useLazyGetServerQuery,
  useLazyGetServersByOrganizationQuery,
  usePostServerMutation,
  usePostSecretClientTokenMutation,
  usePutServerMutation,
  useDeleteServerMutation,
  useLazyGetExportServerInfoQuery,
  useLazyGetEdgeProvisionConfigQuery,
  usePatchEdgeProvisionStatusMutation,
  useGetEdgeEnrollmentsQuery,
  useApproveEdgeEnrollmentMutation,
  useRejectEdgeEnrollmentMutation,
  useGetEdgeAppliancesQuery,
  useCreateEdgeApplianceMutation,
  useDeleteEdgeApplianceMutation,
  useLazyGetEdgeAppliancePackageQuery,
  useLazyGetLatestFactoryImageQuery,
  useGetEdgeUpdateReleasesQuery,
  useCreateEdgeUpdateAssignmentMutation,
} = serverAPI;
